// components/marker/AuctionDetailMarkerComponent.tsx
import styled from 'styled-components';
import { numberWithCommas, formatDate3, transNum } from '../../../../func/func';
import {
  MapCategory,
  MapItemCategory,
} from '../../../search/filter/SampleData';
import { userGradeType } from '../../../../utils/auth';
import { useModal } from '../../../../context/ModalContext';

interface Props {
  data: any;
  onToggle: (data: any) => void;
}

const getCategory = (category: string) => {
  const found = MapItemCategory.find(
    (item) => item.value.split(',').includes(category), // 쉼표(,)로 분리 후 category 포함 여부 확인
  );
  return found ? found.label : '기타'; // 매칭되는 값이 없으면 기본값 반환
};

const convertImg = (img: string) => {
  let imageSrc = '/assets/images/marker/selected_marker_no_Image.svg'; // 기본 이미지 경로

  try {
    // data.img가 있을 경우 띄워쓰기를 %20으로 변환하고 이미지 경로로 사용
    if (img) {
      imageSrc = img.replace(/\s/g, '%20');
    }
  } catch (error) {
    console.error('이미지 경로 처리 중 오류 발생:', error);
  }
  return imageSrc;
};

const handleDetailClick = async (data: any) => {
  const url =
    data.type?.toLowerCase() === 'p'
      ? `/pauction/detail/${data.idx}`
      : data.type?.toLowerCase() === 'e'
        ? `/scheduled/detail/${data.idx}`
        : `/auction/detail/${data.idx}`;
  window.open(url, '_blank');
};

export const AuctionDetailMarkerComponent = ({ data, onToggle }: Props) => {
  if (!data) return null;

  return (
    <Wrapper onClick={() => handleDetailClick(data)}>
      <Header>
        <Label $color="#F45551">경매</Label>
        <Category $color="#F45551">{getCategory(data.item_code)}</Category>
        <Divider />
        <CaseNo>
          {data.caseno}
          {data.unitno}{' '}
        </CaseNo>
      </Header>
      <Body>
        <Info>
          <InfoTop>
            <Address>{data.addr}</Address>
            <RowContent>
              <DateLabel>매각기일</DateLabel>
              <DateValue>{formatDate3(data.date)}</DateValue>
            </RowContent>
          </InfoTop>

          <Prices>
            <RowContent>
              <PriceLabel $color="#616161">감정가</PriceLabel>
              <Price $color="#222">
                {numberWithCommas(data.appraisal_prc, 0)}원
              </Price>
            </RowContent>
            {data.bid_prc ? (
              <RowContent>
                <PriceLabel $color="#F45551">낙찰가</PriceLabel>
                <Price $color="#F45551">
                  {numberWithCommas(data.bid_prc, 0)}원
                </Price>
              </RowContent>
            ) : (
              <RowContent>
                <PriceLabel $color="#008AFF">최저가</PriceLabel>
                <Price $color="#008AFF">
                  {numberWithCommas(data.min_prc, 0)}원
                </Price>
              </RowContent>
            )}
            {data.bid_prc && (
              <RowContent>
                <PriceLabel $color="#F45551">낙찰가율</PriceLabel>
                <Price $color="#F45551">
                  {((data.bid_prc / data.appraisal_prc) * 100).toFixed(2)}%
                </Price>
              </RowContent>
            )}
          </Prices>
          <Toggle
            onClick={(e) => {
              e.stopPropagation();
              onToggle(data);
            }}
          >
            실거래가 확인
          </Toggle>
        </Info>
        <ImageBox>
          <img src={convertImg(data.img)} alt="thumbnail" />
        </ImageBox>
      </Body>
    </Wrapper>
  );
};

export const PlannedPropertyDetailMarkerComponent = ({
  data,
  onToggle,
}: Props) => {
  const { modalDispatch } = useModal();
  if (!data) return null;

  return (
    <Wrapper
      onClick={async () => {
        const userGrade = await userGradeType();
        if (!userGrade) {
          modalDispatch({ type: 'USER_ALERT', payload: 'free' });
          return;
        }
        if (data.addr.includes('*')) {
          modalDispatch({ type: 'USER_ALERT', payload: 'local' });
          return;
        }

        handleDetailClick(data);
      }}
    >
      <Header>
        <Label $color="#C86700">예정</Label>
        <Category $color="#C86700">{getCategory(data.item_code)}</Category>
        <Divider />
        <CaseNo>{data.caseno ?? '업데이트 준비중'}</CaseNo>
      </Header>
      <Body>
        <Info>
          <InfoTop>
            <Address>{data.addr}</Address>
            <Prices>
              <RowContent>
                <DateLabel>접수일</DateLabel>
                <DateValue>
                  {data.dec_date
                    ? formatDate3(data.dec_date)
                    : '업데이트 준비중'}
                </DateValue>
              </RowContent>
              <RowContent>
                <DateLabel>개시일</DateLabel>
                <DateValue>
                  {data.div_date
                    ? formatDate3(data.div_date)
                    : '업데이트 준비중'}
                </DateValue>
              </RowContent>
              <RowContent>
                <DateLabel>배당종기일</DateLabel>
                <DateValue>
                  {data.reg_date
                    ? formatDate3(data.reg_date)
                    : '업데이트 준비중'}
                </DateValue>
              </RowContent>
            </Prices>
          </InfoTop>
          <Toggle
            onClick={(e) => {
              e.stopPropagation();
              onToggle(data);
            }}
          >
            실거래가 확인
          </Toggle>
        </Info>
        <ImageBox>
          <img src={convertImg(data.img)} alt="thumbnail" />
        </ImageBox>
      </Body>
    </Wrapper>
  );
};

export const PAuctionDetailMarkerComponent = ({ data, onToggle }: Props) => {
  if (!data) return null;
  return (
    <Wrapper onClick={() => handleDetailClick(data)}>
      <Header>
        <Label $color="#008AFF">공매</Label>
        <Category $color="#008AFF">{getCategory(data.item_code)}</Category>
        <Divider />
        <CaseNo>{data.caseno}</CaseNo>
      </Header>
      <Body>
        <Info>
          <InfoTop>
            <Address>{data.addr}</Address>
            <RowContent>
              <DateLabel>매각기일</DateLabel>
              <DateValue>{formatDate3(data.date)}</DateValue>
            </RowContent>
          </InfoTop>

          <Prices>
            <RowContent>
              <PriceLabel $color="#616161">감정가</PriceLabel>
              <Price $color="#222">
                {numberWithCommas(data.appraisal_prc, 0)}원
              </Price>
            </RowContent>
            {data.bid_prc ? (
              <RowContent>
                <PriceLabel $color="#F45551">낙찰가</PriceLabel>
                <Price $color="#F45551">
                  {numberWithCommas(data.bid_prc, 0)}원
                </Price>
              </RowContent>
            ) : (
              <RowContent>
                <PriceLabel $color="#008AFF">최저가</PriceLabel>
                <Price $color="#008AFF">
                  {numberWithCommas(data.min_prc, 0)}원
                </Price>
              </RowContent>
            )}
            {data.bid_prc && (
              <RowContent>
                <PriceLabel $color="#F45551">낙찰가율</PriceLabel>
                <Price $color="#F45551">
                  {((data.bid_prc / data.appraisal_prc) * 100).toFixed(2)}%
                </Price>
              </RowContent>
            )}
          </Prices>
          <Toggle
            onClick={(e) => {
              e.stopPropagation();
              onToggle(data);
            }}
          >
            실거래가 확인
          </Toggle>
        </Info>
        <ImageBox>
          <img src={convertImg(data.img)} alt="thumbnail" />
        </ImageBox>
      </Body>
    </Wrapper>
  );
};

export const ComplexItemComponent = ({ data, onToggle }: Props) => {
  if (!data) return null;
  return (
    <Wrapper>
      <Header>
        <Label $color="#00A0B1">실거래</Label>
        <Category $color="#00A0B1">{getCategory(data.item_code)}</Category>
      </Header>
      <Body>
        <Info>
          <InfoTop>
            <Address>{data.addr}</Address>
            <RowContent>
              <DateLabel>거래일</DateLabel>
              <DateValue>{formatDate3(data.date)}</DateValue>
            </RowContent>
          </InfoTop>

          <RowContent>
            <PriceLabel $color="#616161">
              {data.prc ? '매매' : data.monthly_rent ? '월세' : '전세'}
            </PriceLabel>
            <Price $color="#222">
              {data.prc
                ? `${numberWithCommas(data.prc, 0)}원`
                : data.monthly_rent
                  ? `${transNum(data.deposit)}/${transNum(data.monthly_rent)}`
                  : `${numberWithCommas(data.deposit, 0)}`}
            </Price>
          </RowContent>

          <Toggle onClick={() => onToggle(data)}>실거래가 확인</Toggle>
        </Info>
        <ImageBox>
          <img src={convertImg(data.img)} alt="thumbnail" />
        </ImageBox>
      </Body>
    </Wrapper>
  );
};

interface TypeProp {
  $color: string;
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  background: #fff;
`;

const Header = styled.div`
  display: flex;
  padding: 12px 12px 8px 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Label = styled.div<TypeProp>`
  background-color: ${({ $color }) => $color};
  border-radius: 2px;
  display: flex;
  padding: 0 4px;
  justify-content: center;
  align-items: center;

  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const Category = styled.div<TypeProp>`
  font-size: 12px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.24px;
  color: ${({ $color }) => $color};
`;

const Divider = styled.div`
  width: 1px;
  height: 8px;
  background: #e1e2e4;
`;

const CaseNo = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const Body = styled.div`
  display: flex;
  padding: 0 16px 24px 16px;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  flex: 1 0 0;
`;

const InfoTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const Address = styled.div`
  color: #222;
  font-size: 14px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const RowContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const DateLabel = styled.div`
  color: #616161;
`;

const DateValue = styled.div`
  color: #222222;
`;

const Prices = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  align-self: stretch;
`;

const PriceLabel = styled.div<TypeProp>`
  color: ${({ $color }) => $color};

  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.26px;
`;

const Price = styled.div<TypeProp>`
  color: ${({ $color }) => $color};
  font-size: 14px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const ImageBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  /* margin-left: 12px; */

  img {
    width: 64px;
    height: 64px;
    aspect-ratio: 1/1;
    /* object-fit: cover; */
    border-radius: 4px;
  }
`;

const Toggle = styled.button`
  cursor: pointer;
  border: none;
  outline: none;

  display: flex;
  padding: 6px 12px;
  justify-content: center;
  align-items: center;
  gap: 2px;
  border-radius: 6px;
  background: rgba(2, 32, 71, 0.05);

  color: rgba(0, 12, 30, 0.8);
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;
